import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { customInstance } from '../../services/api-client'
import type {
  BankReconciliationAgentRun,
  BankReconciliationConnectorOption,
  BankReconciliationDataSourceConfig,
  BankReconciliationHealth,
  BankReconciliationWorkpaper,
  TabularRows,
} from './types'

const BASE = '/api/bank-reconciliation'

const keys = {
  all: ['bank-reconciliation'] as const,
  config: () => [...keys.all, 'config'] as const,
  connectors: () => [...keys.all, 'connectors'] as const,
  workpaper: (period: string) => [...keys.all, 'workpaper', period] as const,
  table: (tableKey: string, period: string) => [...keys.all, 'table', tableKey, period] as const,
} as const

export function useBankReconciliationWorkpaper(period = 'Apr 2025') {
  return useQuery<BankReconciliationWorkpaper>({
    queryKey: keys.workpaper(period),
    queryFn: () =>
      customInstance<BankReconciliationWorkpaper>({
        url: `${BASE}/workpaper?period=${encodeURIComponent(period)}`,
      }),
    retry: false,
    staleTime: 60_000,
  })
}

export function useBankReconciliationTable(tableKey: string, period = 'Apr 2025', limit = 500) {
  return useQuery<TabularRows>({
    queryKey: [...keys.table(tableKey, period), limit] as const,
    queryFn: () =>
      customInstance<TabularRows>({
        url: `${BASE}/tables/${tableKey}?period=${encodeURIComponent(period)}&limit=${limit}`,
      }),
    retry: false,
    staleTime: 60_000,
  })
}

export function useRunBankReconciliationAgent() {
  const queryClient = useQueryClient()
  return useMutation<BankReconciliationAgentRun, Error, { period: string }>({
    mutationFn: payload =>
      customInstance<BankReconciliationAgentRun>({
        url: `${BASE}/agent/run`,
        method: 'POST',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useBankReconciliationDataSourceConfig(enabled = true) {
  return useQuery<BankReconciliationDataSourceConfig>({
    queryKey: keys.config(),
    queryFn: () =>
      customInstance<BankReconciliationDataSourceConfig>({
        url: `${BASE}/config/data-source`,
      }),
    enabled,
    retry: false,
    staleTime: 60_000,
  })
}

export function useBankReconciliationAvailableConnectors(enabled = true) {
  return useQuery<BankReconciliationConnectorOption[]>({
    queryKey: keys.connectors(),
    queryFn: () =>
      customInstance<BankReconciliationConnectorOption[]>({
        url: `${BASE}/config/available-connectors`,
      }),
    enabled,
    retry: false,
    staleTime: 60_000,
  })
}

export function useSetBankReconciliationDataSource() {
  const queryClient = useQueryClient()
  return useMutation<
    BankReconciliationDataSourceConfig,
    Error,
    { connector_id: string; redshift_schema?: string }
  >({
    mutationFn: payload =>
      customInstance<BankReconciliationDataSourceConfig>({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useClearBankReconciliationDataSource() {
  const queryClient = useQueryClient()
  return useMutation<BankReconciliationDataSourceConfig, Error, void>({
    mutationFn: () =>
      customInstance<BankReconciliationDataSourceConfig>({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all })
    },
  })
}

export function useTestBankReconciliationDataSource() {
  return useMutation<
    BankReconciliationHealth,
    Error,
    { connector_id: string; redshift_schema?: string }
  >({
    mutationFn: payload =>
      customInstance<BankReconciliationHealth>({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: payload,
      }),
  })
}
